import { useCallback, useEffect, useRef } from 'react';
import type { ApiService } from '@admin-console/shared';

/** How often to check whether the JWT needs refreshing. */
const CHECK_INTERVAL_MS = 5 * 60 * 1000;
/** Refresh once the JWT is at least this old (it lasts roughly an hour). */
const REFRESH_AFTER_MINUTES = 45;

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'] as const;

/**
 * Keeps the JWT fresh for users who are actively using the app, so long idle timeouts (2 h, 3 h,
 * Never) don't end in a dead token. Idle users are deliberately not refreshed — the idle timeout
 * governs them. A rejected refresh is reported through ApiService's session-expired handler.
 *
 * @returns refreshNow – forces a refresh (used when the user confirms "Stay Signed In").
 */
export function useTokenKeepAlive(apiService: ApiService): () => void {
  const activeSinceLastCheckRef = useRef(false);

  useEffect(() => {
    const onActivity = () => { activeSinceLastCheckRef.current = true; };
    ACTIVITY_EVENTS.forEach((e) => document.addEventListener(e, onActivity, { passive: true }));

    const timer = setInterval(() => {
      if (!activeSinceLastCheckRef.current) return;
      activeSinceLastCheckRef.current = false;
      void apiService.keepAliveRefresh(REFRESH_AFTER_MINUTES).catch(() => { /* handled via session-expired handler */ });
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((e) => document.removeEventListener(e, onActivity));
      clearInterval(timer);
    };
  }, [apiService]);

  return useCallback(() => {
    void apiService.keepAliveRefresh(0).catch(() => { /* handled via session-expired handler */ });
  }, [apiService]);
}
